import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  MenuItem,
  FormControl,
  Select,
  Button,
  Grid,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Dialog,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  TableCell,
  Tooltip,
  Chip,
} from "@mui/material";
import { Snackbar, Alert } from "@mui/material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import LoadingBar from "react-top-loading-bar";
import InfoIcon from "@mui/icons-material/Info";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// ─── Small formatting/sanitizing helpers ───────────────────────────────────
const sanitizeName = (name) =>
  (name ?? "")
    .replace(/[`'"]/g, "") // strip stray backticks/quotes from bad DB entries
    .trim();

// Normalizes any value used as a lookup key (examType / subExam names).
// CRITICAL: different endpoints represent "no sub-exam" inconsistently —
// GetExamResults uses subExam: "", while GetExamSubCategory_Result returns
// subExam: "--" for the same case. Without unifying these two placeholders
// into the same key, "" !== "--" and every marks lookup for single-category
// exam types (e.g. Term 1) silently fails AND a duplicate/ghost column gets
// created by the backfill safety-net below — which is exactly the bug seen
// in the PDF (two "Term 1" sub-columns, one always blank).
const normKey = (val) => {
  const s = (val ?? "").toString().trim();
  return s === "--" || s === "-" ? "" : s;
};

const formatPercent = (val) => {
  if (val === null || val === undefined || val === "" || isNaN(val)) return "N/A";
  return `${Number(val).toFixed(2)}%`;
};

const formatMarks = (val) => {
  if (val === null || val === undefined || val === "") return "--";
  const num = Number(val);
  return isNaN(num) ? "--" : (Number.isInteger(num) ? String(num) : num.toFixed(2));
};

// Color used for grade text/badges throughout the PDF
const gradeColor = (grade) => {
  switch ((grade || "").toString().trim().toUpperCase()) {
    case "A+":
    case "A":
      return [22, 130, 60]; // green
    case "B+":
    case "B":
      return [24, 100, 140]; // blue-teal
    case "C+":
    case "C":
      return [184, 134, 11]; // amber
    case "D":
      return [204, 102, 0]; // orange
    case "E":
    case "F":
      return [178, 34, 34]; // red
    default:
      return [60, 60, 60]; // neutral gray
  }
};

// Maps API monthId (1-12) to a display name. Used only in the fee-due
// breakdown modal.
const MONTH_NAMES = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatDueDate = (val) => {
  if (!val) return "--";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleDateString();
};

const GeneratedResult = () => {
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [session, setSession] = useState("");
  const [examType, setExamType] = useState("");
  const [className, setClassName] = useState("");
  const [classes, setClasses] = useState([]);
  const [section, setSection] = useState("");
  const [sections, setSections] = useState([]);
  const [resultData, setResultData] = useState([]);
  // Rank lookup for the currently loaded class/section, built from
  // /Result/ClassRanking. Keyed by studentId so both the on-screen table
  // and the PDF can show "Rank X of Y" without an extra call per student.
  const [rankingMap, setRankingMap] = useState({});
  const [totalStudentsInClass, setTotalStudentsInClass] = useState(0);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [permissions, setPermissions] = useState({
    uRead: 0,
    uWrite: 0,
    uModify: 0,
    uDelete: 0,
    isPermission: 0,
  });
  const [formId, setFormId] = useState(null);
  const [error, setError] = useState(null);

  // ─── Fee-check-before-print state ────────────────────────────────────────
  // Clicking "Print" first hits StudentResultWithFeeCheck. If dues are
  // clear (canViewResult: true) we go straight to PDF generation. If dues
  // are pending, we show a confirmation modal with the due breakdown and
  // let the user Cancel or "Print Anyways".
  const [isFeeCheckLoading, setIsFeeCheckLoading] = useState(false);
  const [feeCheckStudentId, setFeeCheckStudentId] = useState(null);
  const [feeCheckDialogOpen, setFeeCheckDialogOpen] = useState(false);
  const [feeCheckData, setFeeCheckData] = useState(null);

  const token = sessionStorage.getItem("token");
  const apiUrl = process.env.REACT_APP_BASE_URL;

  const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 20,
    padding: "8px 24px",
    textTransform: "none",
    fontWeight: 600,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
  }));


  
const sortedResultData = [...resultData].sort((a, b) => {
  const rankA = rankingMap[a.studentId]?.rank ?? Infinity;
  const rankB = rankingMap[b.studentId]?.rank ?? Infinity;
  return rankA - rankB;
});

  // ─── Fetch Form ID ──────────────────────────────────────────────────────────
  const fetchFormId = async () => {
    try {
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/ExclusivePermission/Form_Authorizer",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({ moduleId: 23 }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        const resultForm = data.find((form) => form.formName === "Get Result");
        if (resultForm) {
          setFormId(resultForm.formId);
        } else {
          setError("Get Result form not found in response");
          setIsLoading(false);
        }
      } else {
        setError("Failed to fetch form ID");
        setOpenSnackbar(true);
        setSnackbarMessage("Failed to fetch form ID");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching form ID:", error);
      setError("Error fetching form ID");
      setOpenSnackbar(true);
      setSnackbarMessage("Error fetching form ID");
      setIsLoading(false);
    }
  };

  // ─── Fetch Permissions ──────────────────────────────────────────────────────
  const fetchPermissions = async () => {
    if (!formId) return;
    try {
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ formId }),
      });
      const data = await response.json();
      if (response.ok && data.length > 0) {
        setPermissions(data[0]);
      } else {
        setPermissions({ ...permissions, isPermission: 0 });
        setError("Failed to fetch permissions");
        setOpenSnackbar(true);
        setSnackbarMessage("Failed to fetch permissions");
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setError("Error fetching permissions");
      setOpenSnackbar(true);
      setSnackbarMessage("Error fetching permissions");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setResultData([]);
    setRankingMap({});
    setTotalStudentsInClass(0);
  }, [session, className, section]);

  const fetchSessions = async () => {
    try {
      setLoadingBarProgress(30);
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/ClassPromotion/GetFinancialYear",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
        }
      );
      if (!response.ok) { setLoadingBarProgress(0); return; }
      setLoadingBarProgress(100);
      const data = await response.json();
      if (Array.isArray(data)) {
        setSessions(data);
        // Auto-select the latest session (highest financialYearID)
        if (data.length > 0) {
          const latest = data.reduce((max, s) =>
            s.financialYearID > max.financialYearID ? s : max
          );
          setSession(latest.financialYearID);
        }
      }
    } catch (error) {
      console.error("Error fetching financial years:", error);
    }
  };

  const fetchExamTypes = async () => {
    try {
      const response = await fetch(`${apiUrl}/Exam/ExamList`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) {
        setExamTypes(data.map((item) => ({ id: item.examTypeId, name: item.examType })));
      }
    } catch (error) {
      console.error("Error fetching exam types:", error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) {
        setClasses(data.map((item) => ({ id: item.classId, name: item.className })));
      }
    } catch (error) {
      console.error("Error fetching class names:", error);
    }
  };

  const fetchSections = async (classId) => {
    try {
      const response = await fetch(`${apiUrl}/Exam/ddlSection_clsId`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ classId }),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) {
        setSections(data.map((item) => ({ id: item.sectionId, name: item.sectionName })));
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  // ─── Class ranking (for both the on-screen table and the PDF) ─────────────
  const fetchClassRanking = async (sessionId, classId, sectionId) => {
    try {
      const response = await fetch(`${apiUrl}/Result/ClassRanking`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ sessionId, examTypeId: 0, classId, sectionId }),
      });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching class ranking:", error);
      return [];
    }
  };

  const handleClassChange = (event) => {
    const selectedClass = event.target.value;
    setClassName(selectedClass);
    if (selectedClass) fetchSections(selectedClass);
    else setSections([]);
  };

  const handleGetResult = async () => {
    if (!session || !className || !section) {
      setSnackbarMessage("Please select all fields!");
      setOpenSnackbar(true);
      return;
    }
    try {
      setIsLoading(true);
      const [resultsResponse, rankingList] = await Promise.all([
        fetch("https://wsmsv1.aidigitalsec.com/api/Result/GetStudentResultsGenerated", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({
            sessionId: session,
            examTypeId: 0,
            classId: className,
            sectionId: section,
          }),
        }),
        fetchClassRanking(session, className, section),
      ]);

      if (!resultsResponse.ok) throw new Error(`HTTP error! Status: ${resultsResponse.status}`);
      const data = await resultsResponse.json();

      const map = {};
      rankingList.forEach((r) => {
        map[r.studentId] = r;
      });
      setRankingMap(map);
      setTotalStudentsInClass(rankingList.length);

      if (Array.isArray(data) && data.length > 0) {
        setResultData(data);
      } else {
        setSnackbarMessage("No data available for the selected class.");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Error fetching result data.");
      setOpenSnackbar(true);
      console.error("Error fetching result data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── FEE CHECK — runs before the print/PDF pipeline ────────────────────────
  // Hits StudentResultWithFeeCheck first.
  //   - canViewResult: true  → dues are clear, go straight to PDF generation.
  //   - canViewResult: false → dues are pending, show a confirmation modal
  //     with the due breakdown. User can Cancel (abort) or "Print Anyways"
  //     (proceed to the existing fetchStudentProfile → handlePrint pipeline).
  const handlePrintClick = async (studentId) => {
    if (!session) {
      setSnackbarMessage("Missing session — please reload and try again.");
      setOpenSnackbar(true);
      return;
    }
    setFeeCheckStudentId(studentId);
    setIsFeeCheckLoading(true);
    try {
      const response = await fetch(`${apiUrl}/Result/StudentResultWithFeeCheck`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          studentId,
          sessionId: session,
          examTypeId: 0,
          subExamId: 0,
          subjectId: 0,
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();

      if (data.canViewResult) {
        // Dues are clear — proceed directly to PDF generation.
        setIsFeeCheckLoading(false);
        setFeeCheckStudentId(null);
        fetchStudentProfile(studentId);
      } else {
        // Dues are pending — surface the breakdown and ask for confirmation.
        setFeeCheckData(data);
        setFeeCheckDialogOpen(true);
        setIsFeeCheckLoading(false);
      }
    } catch (error) {
      console.error("Error checking fee status:", error);
      setSnackbarMessage("Error checking fee status. Please try again.");
      setOpenSnackbar(true);
      setIsFeeCheckLoading(false);
      setFeeCheckStudentId(null);
    }
  };

  const handleCancelFeeCheck = () => {
    setFeeCheckDialogOpen(false);
    setFeeCheckData(null);
    setFeeCheckStudentId(null);
  };

  const handlePrintAnyways = () => {
    const studentId = feeCheckStudentId;
    setFeeCheckDialogOpen(false);
    setFeeCheckData(null);
    setFeeCheckStudentId(null);
    if (studentId) fetchStudentProfile(studentId);
  };

  // ─── MAIN PRINT HANDLER ─────────────────────────────────────────────────────
  // Flow:
  //   1. GET student profile  → objData (profile), totalAttendancList[0] (yearly attendance)
  //   2. GET exam results     → results[] (per-subject, per-exam-type rows), totalMarks1[] (per-subject totals)
  //   3. GET exam criteria    → list of examType headers
  //   4. For each exam type, GET sub-categories → sub-exam column names
  //   5. Build PDF from all collected data
  //
  // DATA FIXES applied here:
  //   - normKey() unifies the "--"/"" sub-exam placeholder mismatch between
  //     GetExamResults and GetExamSubCategory_Result — this was the actual
  //     cause of the duplicated/blank "Term 1" column in the PDF.
  //   - The API returns a "ghost" subject row (subjectId: 0, subjectName: "")
  //     which is really a per-exam-type total, not a real subject — it is
  //     filtered out everywhere (subjectTotals, marksData).
  //   - Subject names are sanitized (e.g. "Hindi`" -> "Hindi").
  //   - Exam-wise totals (Term 1 / Term 2 subtotal) are computed directly
  //     from `results` — NOT from the `totalMarks` array, which has no
  //     examType field on its rows and can't be labeled reliably.
  //   - A real overall Pass/Fail status is computed (any subject grade E/F ⇒ FAIL).
  const fetchStudentProfile = async (studentId) => {
    try {
      setIsPdfLoading(true);
      setLoadingBarProgress(30);

      // ── Step 1: Student profile + attendance ──────────────────────────────
      const profileResponse = await fetch(`${apiUrl}/Attendance/StudentProfile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ yearId: session, studentId }),
      });
      if (!profileResponse.ok) throw new Error("Failed to fetch student profile");
      const profileData = await profileResponse.json();

      const studentProfile = profileData.objData;
      const studentAttendance = profileData.totalAttendancList?.[0] ?? null;

      // ── Step 2: Exam results ──────────────────────────────────────────────
      const resultsResponse = await fetch(`${apiUrl}/Result/GetExamResults`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          studentId,
          sessionId: session,
          examTypeId: 0,
          subExamId: 0,
          subjectId: 0,
        }),
      });
      if (!resultsResponse.ok) {
        setLoadingBarProgress(0);
        throw new Error("Failed to fetch exam results");
      }
      setLoadingBarProgress(70);
      const resultsData = await resultsResponse.json();

      const results = resultsData.results ?? [];

      // FIX: drop the "ghost" per-exam-type total row (subjectId 0 / blank name)
      // and sanitize real subject names.
      const subjectTotals = (resultsData.totalMarks1 ?? [])
        .filter((s) => s.subjectName && sanitizeName(s.subjectName) !== "")
        .map((s) => ({ ...s, subjectName: sanitizeName(s.subjectName) }));

      const grossTotal = resultsData.grosstotalMarks?.[0] ?? {};
      const overallGrade = grossTotal.totalGrade ?? "N/A";
      const totalPercentage =
        grossTotal.overallPercentage ?? grossTotal.averagePercentage ?? "N/A";

      const teacherRemarks = results[0]?.teacherRemarks ?? "N/A";

      const subjectNames = subjectTotals.map((s) => s.subjectName);

      // Real overall result status — FAIL if any subject's grade is E/F
      const hasFailingGrade = subjectTotals.some((s) =>
        ["E", "F"].includes((s.totalGrade || "").toString().trim().toUpperCase())
      );
      const resultStatus = hasFailingGrade ? "FAIL" : "PASS";

      // Exam-wise totals (Term 1 / Term 2 ...), derived from `results` since
      // `totalMarks` rows carry no examType/examTypeId to key off of.
      const examWiseTotals = Object.values(
        results.reduce((acc, r) => {
          if (r.subjectId === 0) return acc; // skip ghost total row
          const key = r.examTypeId;
          if (!acc[key]) acc[key] = { examType: normKey(r.examType), obtained: 0, max: 0 };
          acc[key].obtained += Number(r.obtainedMarks) || 0;
          acc[key].max += Number(r.maxMarks) || 0;
          return acc;
        }, {})
      ).map((e) => ({ ...e, percentage: e.max ? +((e.obtained / e.max) * 100).toFixed(2) : 0 }));

      // ── Step 3: Exam criteria ─────────────────────────────────────────────
      const examCriteriaResponse = await fetch(
        `${apiUrl}/Result/GetExamCriteria_result`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({ studentId }),
        }
      );
      if (!examCriteriaResponse.ok) throw new Error("Failed to fetch exam criteria");
      const examCriteriaData = await examCriteriaResponse.json();

      // ── Step 4: Build examHeadersData using GetExamSubCategory_Result API ──
      const subCategoryPromises = examCriteriaData.map(async (exam) => {
        const subCategoryResponse = await fetch(
          `${apiUrl}/Result/GetExamSubCategory_Result`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: token },
            body: JSON.stringify({
              studentId,
              examTypeId: exam.examCategoryId,
            }),
          }
        );

        if (!subCategoryResponse.ok) {
          console.error(`Failed to fetch sub-category for exam type ${exam.examType}`);
          return {
            examType: normKey(exam.examType),
            examCategoryId: exam.examCategoryId,
            subCategories: [],
          };
        }

        const subCategoryData = await subCategoryResponse.json();

        const subCategories = subCategoryData.map((sub) => {
          // normKey collapses "--" (GetExamSubCategory_Result's placeholder)
          // and "" (GetExamResults' placeholder) to the same key, so a
          // single-category exam type (Term 1) produces exactly ONE column
          // instead of the previous duplicate "--"/"Marks" pair.
          const subExamName = normKey(sub.subExam);
          const matchingResult = results.find(
            (r) => r.examTypeId === exam.examCategoryId && normKey(r.subExam) === subExamName
          );
          return {
            name: subExamName,
            maxMarks: matchingResult?.maxMarks ?? sub.maxMarks ?? "",
            minMarks: matchingResult?.minMarks ?? sub.minMarks ?? "",
          };
        });

        return {
          examType: normKey(exam.examType),
          examCategoryId: exam.examCategoryId,
          subCategories,
        };
      });

      const examHeadersData = await Promise.all(subCategoryPromises);

      // ── Step 4b: Safety net — never drop a recorded mark ────────────────────
      // examHeadersData (table COLUMNS) comes from GetExamCriteria_result /
      // GetExamSubCategory_Result, which is a *separate* source of truth from
      // the actual `results` records. If that criteria API is missing an exam
      // type/sub-exam that a student actually has marks for (stale config,
      // retired exam type, etc.), the mark exists in marksData but has no
      // column to render in. To guarantee every real mark is shown, we
      // backfill any examType/subExam combo found in `results` that isn't
      // already covered by the criteria-based headers.
      const existingCombos = new Set();
      examHeadersData.forEach((exam) => {
        exam.subCategories.forEach((sub) => {
          existingCombos.add(`${exam.examType}|||${sub.name}`);
        });
      });

      results.forEach((r) => {
        if (r.subjectId === 0) return; // skip the ghost total row
        const examTypeKey = normKey(r.examType);
        const subExamKey = normKey(r.subExam);
        const comboKey = `${examTypeKey}|||${subExamKey}`;
        if (existingCombos.has(comboKey)) return;

        existingCombos.add(comboKey);
        let examEntry = examHeadersData.find((e) => e.examType === examTypeKey);
        if (!examEntry) {
          examEntry = { examType: examTypeKey, examCategoryId: r.examTypeId, subCategories: [] };
          examHeadersData.push(examEntry);
        }
        examEntry.subCategories.push({
          name: subExamKey,
          maxMarks: r.maxMarks ?? "",
          minMarks: r.minMarks ?? "",
        });
      });

      // ── Step 5: Build marks lookup (keys normalized, not just trimmed) ─────
      const marksData = {};
      results.forEach((r) => {
        // Skip the same ghost row here too (subjectId 0 / blank name)
        if (r.subjectId !== 0 && r.subjectName) {
          const subjectKey = sanitizeName(r.subjectName);
          const examTypeKey = normKey(r.examType);
          const subExamKey = normKey(r.subExam);

          if (!marksData[subjectKey]) marksData[subjectKey] = {};
          if (!marksData[subjectKey][examTypeKey]) marksData[subjectKey][examTypeKey] = {};
          marksData[subjectKey][examTypeKey][subExamKey] = r.obtainedMarks;
        }
      });

      // ── Step 6: Build subjectTotalsMap ───────────────────────────────────
      const subjectTotalsMap = {};
      subjectTotals.forEach((s) => {
        subjectTotalsMap[s.subjectName] = s;
      });

      const updatedStudentProfile = {
        ...studentProfile,
        teacherRemarks,
        grade: overallGrade,
        totalPercentage,
        resultStatus,
      };

      const selectedSession = sessions.find((s) => s.financialYearID === session);
      const sessionYear = selectedSession?.finanacialYear ?? "N/A";

      // Rank + class size, from the ranking list already fetched in handleGetResult
      const rankInfo = rankingMap[studentId];
      const studentRank = rankInfo?.rank ?? null;

      setLoadingBarProgress(100);

      handlePrint(
        updatedStudentProfile,
        studentAttendance,
        subjectNames,
        examHeadersData,
        marksData,
        subjectTotalsMap,
        sessionYear,
        examWiseTotals,
        studentRank,
        totalStudentsInClass
      );
    } catch (error) {
      setIsPdfLoading(false);
      setLoadingBarProgress(0);
      alert("Error generating PDF: " + error.message);
      console.error("API request error:", error);
    }
  };

  // ─── PDF GENERATION — single-page report-card layout ───────────────────────
  const handlePrint = async (
    student,
    attendance,         // totalAttendancList[0] — yearly aggregate
    subjectNames,        // unique, sanitized subject names (from totalMarks1)
    examHeadersData,      // [{ examType, subCategories: [{ name, maxMarks, minMarks }] }]
    marksData,            // marksData[subject][examType][subExam] = obtainedMarks
    subjectTotalsMap,     // totalMarks1 keyed by sanitized subjectName
    sessionYear,
    examWiseTotals,        // [{ examType, obtained, max, percentage }]
    studentRank,           // number | null
    totalStudents          // number
  ) => {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();   // 297
      const pageHeight = doc.internal.pageSize.getHeight(); // 210

      const NAVY = [11, 31, 64];
      const TEAL = [0, 128, 128];
      const GOLD = [190, 150, 40];
      const LIGHT_BG = [245, 247, 250];

      // Outer border
      const borderMargin = 8;
      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.6);
      doc.rect(borderMargin, borderMargin, pageWidth - 2 * borderMargin, pageHeight - 2 * borderMargin);

      // Thin inner accent border for a "certificate" feel
      doc.setDrawColor(190, 171, 108); // gold
      doc.setLineWidth(0.2);
      doc.rect(
        borderMargin + 1.5,
        borderMargin + 1.5,
        pageWidth - 2 * (borderMargin + 1.5),
        pageHeight - 2 * (borderMargin + 1.5)
      );

      // ── Logo & School Header ──────────────────────────────────────────────
      try {
        const placeholderLogo =
          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=";
    doc.setFillColor(255, 255, 255);
        doc.rect(12, 16, 22, 22, "F");
        doc.addImage(placeholderLogo, "PNG", 14, 18, 18, 18);

        const schoolResponse = await fetch(
          "https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName",
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: token },
            body: JSON.stringify({}),
          }
        );
        if (!schoolResponse.ok) throw new Error("School API failed");
        const apiData = await schoolResponse.json();
        const address = apiData?.[0]?.address ?? "N/A";
        const phoneNo = apiData?.[0]?.phoneNo ?? "N/A";
        const schoolName = apiData?.[0]?.schoolName ?? "N/A";

        doc.setFont("times", "bold");
        doc.setFontSize(19);
        doc.text(schoolName.replace(/['"]+/g, ""), pageWidth / 2, 21, null, null, "center");

        doc.setFontSize(9.5);
        doc.setFont("times", "normal");
        doc.setTextColor(80, 80, 80);
        doc.text(address.replace(/['"]+/g, ""), pageWidth / 2, 28, null, null, "center");
        doc.text("Contact No.: " + phoneNo.replace(/['"]+/g, ""), pageWidth / 2, 34, null, null, "center");
        doc.setTextColor(0, 0, 0);

        const logoImg = apiData?.[0]?.logoImg;
        if (logoImg && typeof logoImg === "string" && logoImg.trim() !== "") {
          let cleanedBase64 = logoImg.replace(/^data:image\/[a-zA-Z]+;base64,/, "").replace(/\s|\n/g, "");
          cleanedBase64 += "=".repeat((4 - (cleanedBase64.length % 4)) % 4);
          if (/^[A-Za-z0-9+/=]+$/.test(cleanedBase64)) {
            const logoDataUrl = `data:image/png;base64,${cleanedBase64}`;
            const apiImg = new Image();
            await new Promise((res, rej) => {
              apiImg.onload = res;
              apiImg.onerror = () => rej(new Error("Failed to load logo"));
              apiImg.src = logoDataUrl;
            });
           doc.setFillColor(255, 255, 255);
            doc.rect(12, 16, 22, 22, "F");
            doc.addImage(logoDataUrl, "PNG", 13.5, 18, 19, 19);
          }
        }
      } catch (err) {
        console.error("Logo/school header error:", err.message);
      }

      // PASS / FAIL stamp — top right
      const isPass = (student.resultStatus ?? "").toUpperCase() !== "FAIL";
      const stampColor = isPass ? [22, 130, 60] : [178, 34, 34];
      doc.setFillColor(...stampColor);
      doc.roundedRect(pageWidth - 46, 13, 30, 11, 2, 2, "F");
      doc.setFont("times", "bold");
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(isPass ? "PASS" : "FAIL", pageWidth - 31, 20.5, null, null, "center");

      // RANK badge — directly below the PASS/FAIL stamp
      const rankLabel = studentRank ? `RANK - ${studentRank}` : "RANK N/A";
      doc.setFillColor(...GOLD);
      doc.roundedRect(pageWidth - 46, 25.5, 30, 9, 2, 2, "F");
      doc.setFontSize(9);
      doc.text(rankLabel, pageWidth - 31, 31.3, null, null, "center");
      doc.setTextColor(0, 0, 0);

      // Divider
      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.9);
      doc.line(15, 40, pageWidth - 15, 40);

      // Report title + student name
      doc.setFontSize(11.5);
      doc.setFont("times", "bold");
      doc.text(`Progress Report ${sessionYear}`, pageWidth / 2, 46, null, null, "center");

      doc.setFontSize(14);
      doc.text(`${student.studentName ?? "N/A"}`, pageWidth / 2, 54, null, null, "center");
      doc.setDrawColor(128, 128, 128);
      doc.setLineWidth(0.3);
      doc.line(pageWidth / 2 - 35, 57, pageWidth / 2 + 35, 57);

      // ── Student details panel ──────────────────────────────────────────────
      const panelY = 61;
      const panelH = 24;
      doc.setFillColor(...LIGHT_BG);
      doc.roundedRect(15, panelY, pageWidth - 30, panelH, 2, 2, "F");

      doc.setFont("times", "normal");
      doc.setFontSize(9.5);
      const labelWidth = 30;
      const colLeft = 20;
      const colRight = pageWidth / 2 + 5;

      const leftDetails = [
        ["Father's Name:", student.fathersName ?? "N/A"],
        ["Mother's Name:", student.mothersName ?? "N/A"],
      ];
      const rightDetails = [
        ["Class & Section:", `${student.className ?? "N/A"} - ${student.sectionName ?? "N/A"}`],
        ["Roll No.:", String(student.rollNo ?? "N/A")],
      ];
      const thirdCol = [
        ["Admission No.:", String(student.admissionNo ?? "N/A")],
        ["Date of Birth:", student.studentDOB ?? "N/A"],
      ];

      const lineH = 8;
      let y1 = panelY + 8;
      leftDetails.forEach(([label, value]) => {
        doc.setFont("times", "bold");
        doc.text(label, colLeft, y1);
        doc.setFont("times", "normal");
        doc.text(String(value), colLeft + labelWidth, y1);
        y1 += lineH;
      });

      let y2 = panelY + 8;
      rightDetails.forEach(([label, value]) => {
        doc.setFont("times", "bold");
        doc.text(label, colRight, y2);
        doc.setFont("times", "normal");
        doc.text(String(value), colRight + labelWidth, y2);
        y2 += lineH;
      });

      const colThird = pageWidth - 95;
      let y3 = panelY + 8;
      thirdCol.forEach(([label, value]) => {
        doc.setFont("times", "bold");
        doc.text(label, colThird, y3);
        doc.setFont("times", "normal");
        doc.text(String(value), colThird + labelWidth, y3);
        y3 += lineH;
      });

      // ── Marks Table ───────────────────────────────────────────────────────
      // Columns: S.No | Subject | [ExamType > SubExam ...] | Total (Obt/Max) | Grade
      // (Total Obt and Total Max are combined into one column — the separate
      // pair was redundant table width that isn't needed to read the result.)
      let lastY = panelY + panelH + 5;
      const minMargin = 15;

      if (examHeadersData.length > 0 && subjectNames.length > 0) {
        const headerRow1 = [
          { content: "S.No", rowSpan: 2, styles: { halign: "center" } },
          { content: "Subject", rowSpan: 2, styles: { halign: "center" } },
        ];
        const headerRow2 = [];

        examHeadersData.forEach((exam) => {
          headerRow1.push({
            content: exam.examType,
            colSpan: exam.subCategories.length,
            styles: { halign: "center" },
          });
          exam.subCategories.forEach((sub) => {
            // sub.name is "" for single-category exam types (e.g. Term 1)
            // after the normKey fix — show a sensible label instead of blank.
            const label = sub.name ? sub.name : "Marks";
            headerRow2.push({
              content: `${label}\n(Max:${sub.maxMarks})`,
              styles: { halign: "center" },
            });
          });
        });

        headerRow1.push(
          { content: "Total\n(Obt/Max)", rowSpan: 2, styles: { halign: "center" } },
          { content: "Grade", rowSpan: 2, styles: { halign: "center" } }
        );

        const tableHeaders = [headerRow1, headerRow2];

        const tableData = subjectNames.map((subject, idx) => {
          const row = [idx + 1, subject];

          examHeadersData.forEach((exam) => {
            exam.subCategories.forEach((sub) => {
              const mark = marksData[subject]?.[exam.examType]?.[sub.name];
              row.push(formatMarks(mark));
            });
          });

          const totals = subjectTotalsMap[subject];
          row.push(`${formatMarks(totals?.totalObtainedMarks)} / ${formatMarks(totals?.totalMaxMarks)}`);
          row.push(totals?.totalGrade ?? "--");

          return row;
        });

        const totalColumns =
          2 +
          examHeadersData.reduce((sum, e) => sum + e.subCategories.length, 0) +
          2; // S.No + Subject + Total(Obt/Max) + Grade
        const gradeColIndex = totalColumns - 1;

        const snoColW = 10;
        const subjectColW = 30;
        const totalColW = 24;
        const gradeColW = 16;
        const available = pageWidth - 2 * minMargin - snoColW - subjectColW - totalColW - gradeColW;
        const subExamColW = Math.min(20, available / (totalColumns - 4));
        const tableWidth = snoColW + subjectColW + (totalColumns - 4) * subExamColW + totalColW + gradeColW;
        const marginLeft = (pageWidth - tableWidth) / 2;

        const columnStyles = {
          0: { cellWidth: snoColW },
          1: { cellWidth: subjectColW },
        };
        let colIdx = 2;
        examHeadersData.forEach((exam) => {
          exam.subCategories.forEach(() => {
            columnStyles[colIdx++] = { cellWidth: subExamColW };
          });
        });
        columnStyles[colIdx] = { cellWidth: totalColW };
        columnStyles[colIdx + 1] = { cellWidth: gradeColW };

        doc.autoTable({
          startY: lastY,
          head: tableHeaders,
          body: tableData,
          styles: { fontSize: 8, halign: "center", font: "times", cellPadding: 1.6 },
          headStyles: {
            fillColor: NAVY,
            textColor: [255, 255, 255],
            lineWidth: 0.5,
            font: "times",
            fontStyle: "bold",
            cellPadding: 1.6,
          },
          alternateRowStyles: { fillColor: LIGHT_BG },
          columnStyles,
          theme: "grid",
          margin: { left: marginLeft, right: marginLeft },
          tableWidth,
          didParseCell: (data) => {
            if (data.section === "head" && data.row.index === 1) {
              data.cell.styles.fillColor = TEAL;
            }
            if (data.section === "body" && data.column.index === gradeColIndex) {
              data.cell.styles.textColor = gradeColor(data.cell.raw);
              data.cell.styles.fontStyle = "bold";
            }
          },
        });

        lastY = doc.lastAutoTable.finalY + 5;
      }

      // ── Remarks + Overall Grade (single line) ───────────────────────────
      doc.setFontSize(9.5);
      doc.setFont("times", "bold");
      doc.text("Remarks:", 15, lastY);
      doc.setFont("times", "normal");
      doc.text(`${student.teacherRemarks ?? "N/A"}`, 33, lastY);

      const gradeText = `${student.grade ?? "N/A"} (${formatPercent(student.totalPercentage)})`;
      const rightMargin = 15;
      doc.setFont("times", "bold");
      doc.text("Overall Grade:", pageWidth - rightMargin - doc.getTextWidth(gradeText) - 32, lastY);
      doc.setFont("times", "normal");
      doc.setTextColor(...gradeColor(student.grade));
      doc.text(gradeText, pageWidth - rightMargin, lastY, null, null, "right");
      doc.setTextColor(0, 0, 0);

      lastY += 6;

      // ── Exam-wise totals strip (single line) ─────────────────────────────
      // Computed from `results` (has examTypeId/examType on every row) — the
      // API's own `totalMarks` array has no such field, so it can't be
      // labeled reliably and is intentionally not used here.
      if (examWiseTotals?.length > 0) {
        doc.setFillColor(...LIGHT_BG);
        doc.roundedRect(15, lastY - 4, pageWidth - 30, 8, 1.5, 1.5, "F");
        doc.setFontSize(8.5);
        doc.setFont("times", "bold");
        const summaryText = examWiseTotals
          .map(
            (e) =>
              `${e.examType}:  ${formatMarks(e.obtained)}/${formatMarks(e.max)}  (${formatPercent(
                e.percentage
              )})`
          )
          .join("        |        ");
        doc.text(summaryText, pageWidth / 2, lastY + 1, null, null, "center");
        doc.setFont("times", "normal");
        lastY += 10;
      } else {
        lastY += 4;
      }

      // ── Attendance + Grading Scale, side by side to save vertical space ──
      const sideBySideY = lastY;
      const totalWidth = pageWidth - 2 * minMargin;
      const gap = 6;
      const attendanceWidth = totalWidth * 0.42;
      const gradingWidth = totalWidth - attendanceWidth - gap;
      const attendanceLeft = minMargin;
      const gradingLeft = minMargin + attendanceWidth + gap;

      const attendanceHeaders = [
        [
          { content: "Total Days", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Present", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Absent", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Attendance %", styles: { halign: "center", font: "times", fontStyle: "bold" } },
        ],
      ];
      const attendanceData = [
        [
          attendance?.totalDays ?? "N/A",
          attendance?.presentCount ?? "N/A",
          attendance?.absentCount ?? "N/A",
          attendance?.attendancePercentage != null ? formatPercent(attendance.attendancePercentage) : "N/A",
        ],
      ];

      doc.autoTable({
        startY: sideBySideY,
        head: attendanceHeaders,
        body: attendanceData,
        styles: { fontSize: 8, halign: "center", font: "times", cellPadding: 2 },
        headStyles: { fillColor: NAVY, textColor: [255, 255, 255], font: "times", fontStyle: "bold" },
        theme: "grid",
        margin: { left: attendanceLeft },
        tableWidth: attendanceWidth,
      });
      const attendanceFinalY = doc.lastAutoTable.finalY;

      const gradingHeaders = [
        [
          {
            content: "Marks %",
            styles: { halign: "center", fillColor: NAVY, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5, font: "times" },
          },
          "91-100", "81-90", "71-80", "61-70", "51-60", "41-50", "32-40", "Below 32",
        ],
      ];
      const gradingData = [
        [
          { content: "Grade", styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold", fontSize: 7.5, font: "times" } },
          "A+", "A", "B+", "B", "C+", "C", "D", "E",
        ],
      ];

      doc.autoTable({
        startY: sideBySideY,
        head: gradingHeaders,
        body: gradingData,
        styles: { fontSize: 7.5, halign: "center", cellPadding: 2, font: "times" },
        theme: "grid",
        headStyles: { fillColor: TEAL, textColor: [255, 255, 255], font: "times" },
        tableWidth: gradingWidth,
        margin: { left: gradingLeft },
      });
      const gradingFinalY = doc.lastAutoTable.finalY;

      lastY = Math.max(attendanceFinalY, gradingFinalY) + 12;

      // ── Signature Lines & Footer ─────────────────────────────────────────
      doc.setFont("times", "normal");
      doc.setFontSize(9.5);

      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.3);
      doc.line(15, lastY, 75, lastY);
      doc.text("Signature of Class Teacher", 18, lastY + 5);

      doc.line(pageWidth - 75, lastY, pageWidth - 15, lastY);
      doc.text("Signature of Principal", pageWidth - 18, lastY + 5, null, null, "right");

      // Generation timestamp footer, for authenticity/audit trail
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Generated on ${new Date().toLocaleString()}`,
        pageWidth / 2,
        pageHeight - borderMargin - 3,
        null,
        null,
        "center"
      );
      doc.setTextColor(0, 0, 0);

      setIsPdfLoading(false);
      const pdfUrl = doc.output("bloburl");
      window.open(pdfUrl, "_blank");
    } catch (error) {
      setIsPdfLoading(false);
      alert("Error generating PDF: " + error.message);
      console.error("Error generating PDF:", error);
    }
  };

  const handleSessionChange = (event) => setSession(event.target.value);
  const handleExamTypeChange = (event) => setExamType(event.target.value);
  const handleSectionChange = (event) => setSection(event.target.value);

  useEffect(() => { fetchFormId(); }, []);
  useEffect(() => { if (formId) fetchPermissions(); }, [formId]);
  useEffect(() => {
    if (permissions.isPermission && permissions.uRead) {
      fetchSessions();
      fetchExamTypes();
      fetchClasses();
    }
  }, [permissions]);

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </div>
    );
  }

  if (error || !permissions.isPermission) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Typography variant="h4" color="error">You are not authorised to view this page</Typography>
      </div>
    );
  }

  return (
    <>
      <Box sx={{ backgroundColor: "#0B1F40", padding: "12px 24px", boxShadow: 3, borderRadius: "4px 4px 0 0", margin: "2px auto", maxWidth: "1000px" }}>
        <Typography variant="h4" sx={{ color: "#fff", textAlign: "center", fontWeight: "bold" }}>
          Generated Result{" "}
          <InfoIcon onClick={() => setShowUserGuide(true)} style={{ opacity: "0.6", cursor: "pointer" }} />
        </Typography>
      </Box>

      <Dialog open={showUserGuide} onClose={() => setShowUserGuide(false)} maxWidth="sm" fullWidth
        sx={{ "& .MuiDialog-paper": { borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)", backgroundColor: "#f9fafb" } }}>
        <DialogTitle sx={{ background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)", color: "white", fontWeight: "bold", textAlign: "center", borderRadius: "12px 12px 0 0", py: 2 }}>
          Result User Guide
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Typography variant="h6" sx={{ color: "#1898c2", fontWeight: "600", mb: 2, mt: 1, textAlign: "center" }}>
            Quick Steps to Get and Print Result
          </Typography>
          <Box component="ul" sx={{ listStyleType: "none", padding: 0, "& li": { display: "flex", alignItems: "flex-start", mb: 1.5, fontSize: "0.9rem", color: "#333" }, "& li::before": { content: '"\\2022"', color: "#1898c2", fontWeight: "bold", display: "inline-block", width: "1.5em", mr: 1, fontSize: "1.2rem" }, "& strong": { margin: "0 0.2em" } }}>
            <li>Select a <strong>Session</strong> from the dropdown.</li>
            <li>Select a <strong>Class</strong> from the dropdown.</li>
            <li>Select a <strong>Section</strong> from the dropdown.</li>
            <li>Click <strong>Get Result</strong> to load the student list, with class ranks.</li>
            <li>Click <strong>Print</strong> next to a student to generate their PDF report. If fees are pending, you'll be shown the due breakdown first.</li>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <StyledButton onClick={() => setShowUserGuide(false)} color="primary" variant="contained"
            sx={{ backgroundColor: "#1898c2", "&:hover": { backgroundColor: "#156f9a" }, borderRadius: "8px", px: 4 }}>
            Close
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* ── Fee-due confirmation modal, shown before printing when dues are pending ── */}
      <Dialog
        open={feeCheckDialogOpen}
        onClose={handleCancelFeeCheck}
        maxWidth="sm"
        fullWidth
        sx={{ "& .MuiDialog-paper": { borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#b22222",
            color: "#fff",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon /> Unpaid Fee Dues
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {feeCheckData?.message || "This student's result is blocked due to unpaid fee dues."}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Total Due Amount: ₹{feeCheckData?.totalDueAmount ?? 0}
          </Typography>

          {feeCheckData?.dueMonths?.length > 0 && (
            <Table size="small" sx={{ border: "1px solid #eee" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Total Fee</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Payment Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feeCheckData.dueMonths.map((m, idx) => {
                  const isPaid = m.paidStatus === 1;
                  return (
                    <TableRow key={`${m.monthId}-${idx}`}>
                      <TableCell>{MONTH_NAMES[m.monthId] ?? m.monthId}</TableCell>
                      <TableCell align="right">{m.totalFee}</TableCell>
                      <TableCell align="right">{m.paidAmount}</TableCell>
                      <TableCell align="right">{m.balance}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={isPaid ? "Paid" : "Unpaid"}
                          size="small"
                          color={isPaid ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell align="center">{formatDueDate(m.feePaymentDate)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={handleCancelFeeCheck} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handlePrintAnyways} variant="contained" color="error">
            Print Anyways
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={3} style={{ padding: "20px", margin: "10px auto", maxWidth: "1000px" }}>
        <LoadingBar progress={loadingBarProgress} color="rgb(152, 106, 182)" height={5} />

        <Grid container spacing={2} justifyContent="center" sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <Select value={session} onChange={handleSessionChange} displayEmpty sx={{ height: 45 }}>
                <MenuItem value="" disabled>Select Session</MenuItem>
                {sessions.map((item) => (
                  <MenuItem key={item.financialYearID} value={item.financialYearID}>{item.finanacialYear}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2} justifyContent="center" sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <Select value={className} onChange={handleClassChange} displayEmpty sx={{ height: 45 }}>
                <MenuItem value="" disabled>Select Class</MenuItem>
                {classes.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <Select value={section} onChange={handleSectionChange} displayEmpty sx={{ height: 45 }} disabled={!className}>
                <MenuItem value="" disabled>Select Section</MenuItem>
                {sections.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button variant="contained" onClick={handleGetResult} sx={{ width: "200px", height: 45 }}>
            Get Result
          </Button>
        </Box>

        <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert onClose={() => setOpenSnackbar(false)} severity="warning" sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Box sx={{ mt: 3 }}>
          {permissions.uRead ? (
            isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="150px">
                <CircularProgress color="primary" />
                <Typography variant="body1" sx={{ ml: 2 }}>Fetching result data...</Typography>
              </Box>
            ) : resultData.length > 0 ? (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>Result Data</Typography>
                <Table sx={{ border: "1px solid #ccc" }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell align="center">Rank</TableCell>
                      <TableCell align="center">Roll No</TableCell>
                      <TableCell align="center">Student Name</TableCell>
                      <TableCell align="center">Total Marks</TableCell>
                      <TableCell align="center">Obtained Marks</TableCell>
                      <TableCell align="center">Grade</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedResultData.map((student, index) => {
                      const rankInfo = rankingMap[student.studentId];
                      const isThisStudentFeeChecking =
                        isFeeCheckLoading && feeCheckStudentId === student.studentId;
                      return (
                        <TableRow key={index} sx={{ "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" }, "&:nth-of-type(even)": { backgroundColor: "#ffffff" } }}>
                          <TableCell align="center">
                            {rankInfo?.rank ? (
                              <Chip
                                icon={rankInfo.rank <= 3 ? <EmojiEventsIcon fontSize="small" /> : undefined}
                                label={rankInfo.rank}
                                size="small"
                                color={rankInfo.rank <= 3 ? "warning" : "default"}
                                sx={{ fontWeight: 700 }}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell align="center">{student.rollNo}</TableCell>
                          <TableCell align="center">{student.studentName}</TableCell>
                          <TableCell align="center">{student.totalMaxMarks}</TableCell>
                          <TableCell align="center">{student.totalObtainedMarks}</TableCell>
                          <TableCell align="center">{student.grade}</TableCell>
                          <TableCell align="center">
                            <Tooltip title={permissions.uWrite ? "" : "You are not authorised to print results"}>
                              <span>
                                <Button
                                  onClick={() => handlePrintClick(student.studentId)}
                                  style={{
                                    padding: "5px 14px",
                                    backgroundColor: permissions.uWrite ? "#007BFF" : "#ccc",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "5px",
                                    cursor: permissions.uWrite ? "pointer" : "not-allowed",
                                    minWidth: "56px",
                                  }}
                                  disabled={!permissions.uWrite || isThisStudentFeeChecking}
                                >
                                  {isThisStudentFeeChecking ? (
                                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                                  ) : (
                                    "Print"
                                  )}
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>No data to display.</Typography>
            )
          ) : (
            <Typography variant="h6" sx={{ textAlign: "center", mt: 2 }}>You are not authorised to view results</Typography>
          )}
        </Box>
      </Paper>

      <Dialog open={isPdfLoading}
        PaperProps={{ style: { background: "transparent", boxShadow: "none", overflow: "hidden" } }}
        sx={{ "& .MuiDialog-container": { alignItems: "center", justifyContent: "center" } }}>
        <DialogContent sx={{ background: "white", padding: "20px", borderRadius: "8px", textAlign: "center", minWidth: "200px" }}>
          <div style={{ width: "40px", height: "40px", border: "4px solid #f3f3f3", borderTop: "4px solid #3498db", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 15px" }} />
          <Typography>Generating PDF, please wait...</Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GeneratedResult;